import { Card, Link } from '@chakra-ui/react';
import {
  CustomFieldEntityEnum,
  CustomFieldPermissionsEnum,
  CustomFieldTypeEnum,
  IBillGroupResp,
  IContractWithQuotes,
  ICustomFieldRecordSchema,
  ICustomFieldResSchema,
  IInvoiceRespSchema,
  InvoiceStatusEnum,
  InvoiceTypes,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { toDateShort } from '@monetize/utils/core';
import { useAtomValue } from 'jotai';
import { MdEdit } from 'react-icons/md';
import { Link as ReactDomLink } from 'react-router-dom';
import { useGetById } from '../../api/queryUtils';
import {
  MBox,
  MCheckbox,
  MCustomIconButton,
  MFlex,
  MLink,
  MSimpleGrid,
  MSpinner,
  MText,
} from '../../components/Monetize';
import { MTooltip } from '../../components/Monetize/chakra';
import { DateDisplay } from '../../components/Monetize/DateDisplay';
import { INVOICE_VOID_REASON_CODE_DISPLAY } from '../../constants/invoice';
import {
  getBillGroupPageRoute,
  getInvoiceDetailContractsEdit,
  getQuoteEditRoute,
} from '../../constants/routes';
import { useCustomFields } from '../../hooks/useCustomFields';
import { getResolvedPermissions } from '../../utils/customFields';
import { useACL } from '../../services/acl/acl';
import { appGlobalDataState } from '../../store/global.store';
import { hasEditableInvoiceContent } from './EditInvoiceModal.utils';

type EditInvoiceSectionProps = {
  isLoading: boolean;
  isDraftInvoice?: boolean;
  invoice: IInvoiceRespSchema;
  customFiledValues?: ICustomFieldRecordSchema;
  onOpenEditInvoiceModal: () => void;
};

export const EditInvoiceSection = ({
  isLoading,
  isDraftInvoice,
  invoice,
  customFiledValues,
  onOpenEditInvoiceModal,
}: EditInvoiceSectionProps) => {
  const { hasMultipleLegalEntity } = useAtomValue(appGlobalDataState);

  const { data: billGroup } = useGetById<IBillGroupResp>(
    'billGroups',
    invoice.billGroupId,
  );

  const { data: contractAndQuoteData } = useGetById<
    IContractWithQuotes,
    { quoteDescription: Maybe<string>; contractName: string }
  >('contracts', invoice.contractId!, {
    enabled: !!invoice.contractId,
    select: (data) => ({
      quoteDescription: data.quotes.find(
        (quote) => quote.id === invoice.quoteId,
      )?.description,
      contractName: `${toDateShort(data.startDate)} - ${toDateShort(data.endDate)}`,
    }),
  });

  const { customFieldList, isLoading: isCustomFieldsLoading } = useCustomFields(
    CustomFieldEntityEnum.INVOICE,
  );

  const { canDo } = useACL();
  const canUpdateBilling = canDo([['billing', 'update']]);

  const stripHtmlTags = (html: string | null) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html ?? 'Blank', 'text/html');

    return doc.body.textContent;
  };

  if (isLoading || isCustomFieldsLoading) {
    return (
      <MBox w="100%" display="flex" alignItems="center" justifyContent="center">
        <MSpinner size="sm" />
      </MBox>
    );
  }

  // Join only the populated parts so an address with an id but empty
  // city/country doesn't render a lone comma.
  const billingCityCountry = [
    invoice.billingAddress?.city,
    invoice.billingAddress?.country,
  ]
    .filter(Boolean)
    .join(', ');

  const shippingCityCountry = [
    invoice.shippingAddress?.city,
    invoice.shippingAddress?.country,
  ]
    .filter(Boolean)
    .join(', ');

  const showInvoicingDate =
    invoice.invoiceType === InvoiceTypes.ONETIME &&
    invoice.status !== InvoiceStatusEnum.UNPAID;

  const isVoided = invoice.status === InvoiceStatusEnum.VOID;

  /**
   * Once the invoice is locked, custom fields are the only thing the modal
   * still lets through. With none the user can write to, it would open an
   * empty body over a Save that writes nothing, so the entry point is
   * disabled instead. Read-only fields do not count - the form renders them
   * disabled, which is the same dead end.
   */
  const canEditInvoice = hasEditableInvoiceContent({
    status: invoice.status,
    canUpdateBilling,
    hasEditableCustomFields: customFieldList.some((customField) =>
      getResolvedPermissions(customField).includes(
        CustomFieldPermissionsEnum.WRITE,
      ),
    ),
  });
  const isEditDisabled = isVoided || !canEditInvoice;
  const editDisabledReason = canUpdateBilling
    ? `There are no editable details on a ${invoice.status.toLowerCase()} invoice.`
    : 'You do not have permission to edit invoice details.';

  return (
    <Card.Root variant="edit" width="100%">
      <Card.Header>
        <MFlex justifyContent="space-between" alignItems="center">
          Invoice Details
          <MTooltip
            label={editDisabledReason}
            isDisabled={isVoided || canEditInvoice}
            shouldWrapChildren
          >
            <MCustomIconButton
              variant="icon"
              btnSize={5}
              icon={MdEdit}
              aria-label="Edit invoice details"
              data-testid="edit-draft-invoice-btn"
              isDisabled={isEditDisabled}
              onClick={onOpenEditInvoiceModal}
            />
          </MTooltip>
        </MFlex>
      </Card.Header>
      <Card.Body>
        <MSimpleGrid
          columns={{
            base: 1,
            sm: 2,
            md: 3,
            lg: 4,
            xl: 5,
          }}
          gap={4}
          mb={6}
          alignItems="flex-start"
        >
          {hasMultipleLegalEntity && invoice.fromCompany?.name && (
            <MBox>
              <MText color="tGray.darkPurple" fontWeight={400}>
                Legal Entity
              </MText>
              <MText color="tPurple.200">{invoice.fromCompany.name}</MText>
            </MBox>
          )}
          {invoice.contractId && (
            <MBox>
              <MText color="tGray.darkPurple" fontWeight={400}>
                Contract
              </MText>
              <Link
                fontSize="sm"
                _hover={{ textDecoration: 'underline' }}
                asChild
              >
                <ReactDomLink
                  to={getInvoiceDetailContractsEdit(invoice.contractId)}
                >
                  {contractAndQuoteData?.contractName ?? invoice.contractId}
                </ReactDomLink>
              </Link>
            </MBox>
          )}
          {invoice.quoteId && (
            <MBox>
              <MText color="tGray.darkPurple" fontWeight={400}>
                Quote
              </MText>
              <Link
                fontSize="sm"
                _hover={{ textDecoration: 'underline' }}
                maxWidth="200px"
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
                display="block"
                asChild
              >
                <ReactDomLink to={getQuoteEditRoute(invoice.quoteId)}>
                  {contractAndQuoteData?.quoteDescription ?? invoice.contractId}
                </ReactDomLink>
              </Link>
            </MBox>
          )}

          {isDraftInvoice && (
            <>
              <MBox>
                <MText color="tGray.darkPurple" fontWeight={400}>
                  Billing Contact *
                </MText>
                <MText color="tPurple.200">
                  {invoice.billingContact.fullName ?? 'Blank'}
                </MText>
              </MBox>
              <MBox>
                <MText color="tGray.darkPurple" fontWeight={400}>
                  Billing Address
                </MText>
                {invoice.billingAddress?.id ? (
                  <>
                    {invoice.billingAddress.line1 && (
                      <MText>{invoice.billingAddress.line1}</MText>
                    )}
                    {invoice.billingAddress.line2 && (
                      <MText>{invoice.billingAddress.line2}</MText>
                    )}
                    {billingCityCountry ? (
                      <MText>{billingCityCountry}</MText>
                    ) : (
                      <MText>Blank</MText>
                    )}
                  </>
                ) : (
                  <MText>Blank</MText>
                )}
              </MBox>
              <MBox>
                <MText color="tGray.darkPurple" fontWeight={400}>
                  Shipping Contact *
                </MText>
                <MText color="tPurple.200">
                  {invoice.shippingContact.fullName ?? 'Blank'}
                </MText>
              </MBox>
              <MBox>
                <MText color="tGray.darkPurple" fontWeight={400}>
                  Shipping Address
                </MText>
                {invoice.shippingAddress?.id ? (
                  <>
                    {invoice.shippingAddress.line1 && (
                      <MText>{invoice.shippingAddress.line1}</MText>
                    )}
                    {invoice.shippingAddress.line2 && (
                      <MText>{invoice.shippingAddress.line2}</MText>
                    )}
                    {shippingCityCountry ? (
                      <MText>{shippingCityCountry}</MText>
                    ) : (
                      <MText>Blank</MText>
                    )}
                  </>
                ) : (
                  <MText>Blank</MText>
                )}
              </MBox>
            </>
          )}

          {showInvoicingDate && (
            <MBox>
              <MText color="tGray.darkPurple" fontWeight={400}>
                Invoicing Date
              </MText>
              <MText color="tPurple.200">
                {invoice.invoicingDate
                  ? toDateShort(invoice.invoicingDate)
                  : 'Blank'}
              </MText>
            </MBox>
          )}
          <MBox>
            <MText color="tGray.darkPurple" fontWeight={400}>
              PO Number
            </MText>
            <MText color="tPurple.200">
              {invoice.purchaseOrderNumber ?? 'Blank'}
            </MText>
          </MBox>
          <MBox>
            <MText color="tGray.darkPurple" fontWeight={400}>
              Registration Number
            </MText>
            <MText color="tPurple.200">
              {invoice.registrationNumber ?? 'Blank'}
            </MText>
          </MBox>
          {invoice.billGroupId && (
            <MBox>
              <MText color="tGray.darkPurple" fontWeight={400}>
                Bill Group
              </MText>
              <Link
                fontSize="sm"
                _hover={{ textDecoration: 'underline' }}
                maxWidth="200px"
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
                display="block"
                asChild
              >
                <ReactDomLink to={getBillGroupPageRoute(invoice.billGroupId)}>
                  {billGroup?.name ?? invoice.billGroupId}
                </ReactDomLink>
              </Link>
            </MBox>
          )}
          <MBox>
            <MText color="tGray.darkPurple" fontWeight={400}>
              Invoice Memo
            </MText>
            <MText color="tPurple.200" truncate>
              {stripHtmlTags(invoice.invoiceMemo)}
            </MText>
          </MBox>
          <MBox>
            <MText color="tGray.darkPurple" fontWeight={400}>
              Invoice Terms
            </MText>
            <MText color="tPurple.200" truncate>
              {stripHtmlTags(invoice.invoiceTerms ?? null)}
            </MText>
          </MBox>
          <MBox>
            <MText color="tGray.darkPurple" fontWeight={400}>
              VAT Number
            </MText>
            <MText color="tPurple.200">{invoice.vatNumber ?? 'Blank'}</MText>
          </MBox>
          {invoice.status === InvoiceStatusEnum.VOID && (
            <>
              <MBox>
                <MText color="tGray.darkPurple" fontWeight={400}>
                  Voided By
                </MText>
                <MText color="tPurple.200">{invoice.voidedBy ?? 'Blank'}</MText>
              </MBox>
              <MBox>
                <MText color="tGray.darkPurple" fontWeight={400}>
                  Voided On
                </MText>
                {invoice.voidDate ? (
                  <DateDisplay
                    date={invoice.voidDate}
                    timezoneConfigKey="userTimezone"
                    textProps={{ color: 'tPurple.200' }}
                  />
                ) : (
                  <MText color="tPurple.200">Blank</MText>
                )}
              </MBox>
              <MBox>
                <MText color="tGray.darkPurple" fontWeight={400}>
                  Reason Code
                </MText>
                <MText color="tPurple.200">
                  {invoice.voidReasonCode
                    ? INVOICE_VOID_REASON_CODE_DISPLAY[invoice.voidReasonCode]
                    : 'Blank'}
                </MText>
              </MBox>
              <MBox>
                <MText color="tGray.darkPurple" fontWeight={400}>
                  Internal Comment
                </MText>
                <MText color="tPurple.200" truncate>
                  {invoice.voidComment ?? 'Blank'}
                </MText>
              </MBox>
            </>
          )}
          {customFieldList?.length < 5 &&
            customFieldList?.map((customField: ICustomFieldResSchema) => {
              const { key, displayLabel, type } = customField;
              const fieldValue = customFiledValues![key];

              switch (type) {
                case CustomFieldTypeEnum.CHECKBOX: {
                  return (
                    <MBox key={key}>
                      <MCheckbox isChecked={!!fieldValue}>
                        {displayLabel}
                      </MCheckbox>
                    </MBox>
                  );
                }
                default:
                  return (
                    <MBox key={key}>
                      <MText color="tGray.darkPurple" fontWeight={400}>
                        {displayLabel}
                      </MText>
                      <MText color="tPurple.200">{fieldValue ?? 'Blank'}</MText>
                    </MBox>
                  );
              }
            })}
        </MSimpleGrid>

        {customFieldList?.length >= 5 && (
          <MBox>
            <MText color="tGray.darkPurple" fontWeight={400}>
              Additional Fields
            </MText>
            <MLink
              onClick={onOpenEditInvoiceModal}
              fontSize="sm"
              cursor="pointer"
            >
              View All in Edit screen
            </MLink>
          </MBox>
        )}
      </Card.Body>
    </Card.Root>
  );
};
